import { useEffect } from 'react';
import {
  ITerminalStrategyError,
  StoredReader,
  TerminalActions,
  TerminalPaymentStatus,
} from '@frontend/payments-terminal-controller';
import { createStoreWithSubscribe } from '@frontend/store';
import {
  useInvalidateDiscoverReaderQuery,
  useOptimisticResetDiscoverReaderQuery,
} from '../hooks/use-discover-readers-query';

export type ReaderState = {
  reader: StoredReader;
  actions: TerminalActions;
  status: TerminalPaymentStatus;
  paymentIntentId: string | null;
  canceledStatus: null | 'in_progress' | 'complete'; // null means not starting cancelation
  isCancelingPromise: Promise<void> | null;
  paymentStatus: null | 'starting' | 'collecting' | 'complete';
  error: ITerminalStrategyError | null;
};

export type ReaderStateForSetter = Partial<Omit<ReaderState, 'reader'>> & Pick<ReaderState, 'actions'>;
export type ReaderStateForUpdater = Partial<Omit<ReaderState, 'reader'>>;

type TerminalStatusState = {
  activeReaderId: string | null;
  nextReaderId: string | null;
  readers: Record<string, ReaderState>;
};

type TerminalStatusActions = {
  getReaderState: (readerId: string) => ReaderState | null;
  getActiveReader: () => ReaderState | null;
  unsetActiveReader: () => void;
  setActiveReader: (reader: StoredReader, extraState: ReaderStateForSetter) => Promise<void>;
  clearAllReaders: () => Promise<void>;
  setReaderStatus: (readerId: string, toSet: { status: TerminalPaymentStatus; error?: ReaderState['error'] }) => void;
  canCancelReaderPayment: (readerId: string) => boolean;
  cancelOngoingPayment: (readerId: string) => Promise<boolean>;
  isCollectingPayment: (readerId?: string) => boolean;
  isActiveReader: (readerId: string) => boolean;
  getAllReaders: () => Record<string, ReaderState>;
  hasActiveReader: () => boolean;
  isReaderInRestrictedState: (readerId: string) => boolean;
  cancelAllOngoingPayments: () => Promise<void>;
  isCanceledOrCanceling: (readerId: string) => boolean;
  setInitializingReaderId: (readerId: string | null) => void;
  isInitializingReader: () => boolean;
  isPaymentIntentStale: (paymentIntentId: string) => boolean;
  activeReaderMethods: {
    isInRestrictedState: () => boolean;
    setErrorState: (error: ITerminalStrategyError | null) => void;
    setStartCollecting: (paymentStatus: Extract<ReaderState['paymentStatus'], 'starting' | 'collecting'>) => void;
    setStopCollecting: (paymentStatus: Extract<ReaderState['paymentStatus'], 'complete'> | null) => void;
    isCollectingPayment: () => boolean;
    isIntentCancelled: (paymentIntentId: string) => boolean;
    setForRetryPayment: () => void;
    hasStartedCollectingPayment: () => boolean;
    isStartingCollection: () => boolean;
    isCanceledOrCanceling: () => boolean;
  };
};

export type TerminalStatusStore = TerminalStatusState & TerminalStatusActions;

export const RESTRICTED_TERMINAL_STATES: TerminalPaymentStatus[] = ['success', 'processing'];

const terminalStatusStore = createStoreWithSubscribe<TerminalStatusStore>(
  (set, get) => {
    /********************
     * INTERNAL METHODS
     ********************/

    /**
     * Use for initial setup, or to completely reset reader state
     */
    const _setReaderState = (reader: StoredReader, extraState: ReaderStateForSetter) => {
      const { readers } = get();
      set({
        readers: {
          ...readers,
          [reader.readerId]: {
            reader,
            status: 'not_ready',
            paymentIntentId: null,
            canceledStatus: null,
            isCancelingPromise: null,
            paymentStatus: null,
            error: null,
            ...extraState,
          },
        },
      });
    };

    /**
     * Use to patch/update selected fields of reader state.
     * Keys not included will not be modified.
     */
    const _updateReaderState = (reader: StoredReader, toUpdate: ReaderStateForUpdater) => {
      const { readers } = get();
      const currentState = readers[reader.readerId];
      if (!currentState) {
        throw new Error(`Reader with id ${reader.readerId} is not registered, can't update state`);
      }

      set({
        readers: {
          ...readers,
          [reader.readerId]: {
            ...currentState,
            ...toUpdate,
          },
        },
      });
    };

    /**
     * Will attempt to cancel ongoing payment for reader
     * - Set updateReaderState to false to avoid updating the reader state in the store, when making sure a payment is cancelled.
     */
    const _maybeCancelOngoingPayment = async (
      reader: StoredReader,
      currentState: ReaderState,
      updateReaderState = true
    ) => {
      if (currentState.isCancelingPromise) {
        // already canceling, wait for it to complete
        await currentState.isCancelingPromise;
        return;
      }

      const canCancel = canCancelReaderPayment(reader.readerId);
      if (!canCancel) {
        return false;
      }

      if (updateReaderState) {
        await cancelOngoingPayment(currentState.reader.readerId);
      } else {
        await currentState.actions.cancelCurrentAction();
      }

      return true;
    };

    /********************
     * GETTER METHODS
     ********************/

    /**
     * Get reader by readerId
     */
    const getReaderState = (readerId: string) => {
      const { readers } = get();
      return readers[readerId] || null;
    };

    /**
     * Get active Reader
     */
    const getActiveReader = () => {
      const { activeReaderId } = get();
      return (activeReaderId && getReaderState(activeReaderId)) || null;
    };

    const getAllReaders = () => {
      const { readers } = get();
      return readers;
    };

    /**
     * Uses terminal actions to cancel an ongoing payment.
     * Updates reader state accordingly.
     */
    const cancelOngoingPayment = async (readerId: string) => {
      const canCancel = canCancelReaderPayment(readerId);

      if (!canCancel) {
        return false;
      }

      const readerState = getReaderState(readerId);
      if (!readerState) {
        throw new Error(`cancelOngoingPayment(): Reader with id ${readerId} is not registered, can't cancel payment`);
      }

      const promise = readerState.actions.cancelCurrentAction();

      _updateReaderState(readerState.reader, {
        isCancelingPromise: promise,
        canceledStatus: 'in_progress',
        paymentStatus: null,
        status: 'not_ready',
        error: null,
      });

      await promise;

      _updateReaderState(readerState.reader, {
        isCancelingPromise: null,
        canceledStatus: 'complete',
        // DO NOT CLEAR paymentIntentId. We need to check from current to make sure it has been updated
      });

      return true;
    };

    /************************
     * SETTER METHODS
     ************************/

    /**
     * Sets the reader state, as well as activeReaderId and nextReaderId.
     * If nextReaderId is set, it means we are in the process of initializing a new reader.
     */
    const setActiveReader = async (reader: StoredReader, extraState: ReaderStateForSetter) => {
      _setReaderState(reader, extraState);
      set({ activeReaderId: reader.readerId, nextReaderId: null });
    };

    const unsetActiveReader = () => {
      const activeReader = getActiveReader();
      if (!activeReader) {
        return;
      }

      _updateReaderState(activeReader.reader, {
        status: 'not_ready',
      });

      set({ activeReaderId: null });
    };

    /**
     * Clear all registered readers - Use on unmount
     * - Cancels them if they have ongoing payments
     */
    const clearAllReaders = async () => {
      const { readers } = get();

      await Promise.all(
        Object.values(readers).map(async (readerState) => {
          await _maybeCancelOngoingPayment(readerState.reader, readerState, false);
        })
      );

      set({ readers: {}, activeReaderId: null });
    };

    /**
     * Cancels all ongoing payments from all registered readers
     */
    const cancelAllOngoingPayments = async () => {
      const { readers } = get();

      await Promise.all(
        Object.values(readers).map(async (readerState) => {
          await _maybeCancelOngoingPayment(readerState.reader, readerState);
        })
      );
    };

    /**
     * Set Reader Status
     */
    const setReaderStatus = (
      readerId: string,
      { status, error }: { status: TerminalPaymentStatus; error?: ReaderState['error'] }
    ) => {
      const readerState = getReaderState(readerId);
      if (!readerState) {
        throw new Error(`Reader with id ${readerId} is not registered, can't set status`);
      }

      if (isReaderInRestrictedState(readerId)) {
        return false;
      }

      if (isCanceledOrCanceling(readerId) && status === 'error') {
        return false;
      }

      if (isInitializingReader()) {
        return;
      }

      if (
        readerState.status === status ||
        (status === 'error' && JSON.stringify(readerState.error) === JSON.stringify(error))
      ) {
        /**
         * Optimization
         * Don't set if the status is the same, or if error status and is the same error
         */
        return false;
      }

      _updateReaderState(readerState.reader, { status, error });

      return true;
    };

    const setErrorState = (readerId: string, error: ITerminalStrategyError | null) => {
      const readerState = getReaderState(readerId);
      if (!readerState) {
        throw new Error(`Reader with id ${readerId} is not registered, can't set error state`);
      }

      _updateReaderState(readerState.reader, { status: 'not_ready', error });
    };

    const setStartCollecting = (
      readerId: string,
      paymentStatus: Extract<ReaderState['paymentStatus'], 'starting' | 'collecting'>
    ) => {
      const readerState = getReaderState(readerId);
      if (!readerState) {
        throw new Error(`Reader with id ${readerId} is not registered, can't set start collecting state`);
      }

      _updateReaderState(readerState.reader, { paymentStatus, error: null });
    };

    const setStopCollecting = (
      readerId: string,
      paymentStatus: Extract<ReaderState['paymentStatus'], 'complete'> | null
    ) => {
      const readerState = getReaderState(readerId);
      if (!readerState) {
        throw new Error(`Reader with id ${readerId} is not registered, can't set stop collecting state`);
      }

      _updateReaderState(readerState.reader, { paymentStatus });
    };

    const setForRetryPayment = (readerId: string) => {
      const readerState = getReaderState(readerId);
      if (!readerState) {
        throw new Error(`Reader with id ${readerId} is not registered, can't set for retry payment`);
      }

      _maybeCancelOngoingPayment(readerState.reader, readerState);

      _updateReaderState(readerState.reader, {
        paymentIntentId: null,
        paymentStatus: null,
        canceledStatus: null,
        isCancelingPromise: null,
        error: null,
      });
    };

    const setInitializingReaderId = (readerId: string | null) => {
      set({ nextReaderId: readerId });
    };

    /************************
     * CHECK STATUS METHODS
     ************************/

    const isPaymentIntentStale = (paymentIntentId: string) => {
      const { readers, activeReaderId } = get();

      const isStale = Object.values(readers).some(
        (readerState) =>
          readerState.paymentIntentId === paymentIntentId && readerState.reader.readerId !== activeReaderId
      );

      return isStale;
    };

    const isInitializingReader = () => {
      const { nextReaderId, activeReaderId } = get();

      return nextReaderId !== null && nextReaderId !== activeReaderId;
    };

    /**
     * Check if a reader is available to be cancelled
     */
    const canCancelReaderPayment = (readerId: string) => {
      const readerState = getReaderState(readerId);
      if (!readerState) {
        throw new Error(`Reader with id ${readerId} is not registered, can't check payment status`);
      }

      if (!!readerState.canceledStatus) {
        /**
         * If cancelling or cancelled, don't cancel again
         */
        return false;
      }

      /**
       * If paymentStatus is complete, but the status is not restricted,
       * it can be cancelled.
       */
      return (
        readerState.paymentStatus === 'collecting' ||
        (readerState.paymentStatus === 'complete' && !RESTRICTED_TERMINAL_STATES.includes(readerState.status))
      );
    };

    const isReaderInRestrictedState = (readerId: string) => {
      const readerState = getReaderState(readerId);
      if (!readerState) {
        return false;
      }

      return RESTRICTED_TERMINAL_STATES.includes(readerState.status);
    };

    const isCollectingPayment = (readerId?: string) => {
      const activeReaderId = readerId || get().activeReaderId;
      if (!activeReaderId) return false;

      const readerState = getReaderState(activeReaderId);
      if (!readerState) return false;

      return readerState.paymentStatus === 'collecting';
    };

    const hasStartedCollectingPayment = (readerId?: string) => {
      const activeReaderId = readerId || get().activeReaderId;
      if (!activeReaderId) return false;

      const readerState = getReaderState(activeReaderId);

      if (!readerState) return false;

      return readerState.paymentStatus === 'starting' || readerState.paymentStatus === 'collecting';
    };

    const isStartingCollection = (readerId?: string) => {
      const activeReaderId = readerId || get().activeReaderId;
      if (!activeReaderId) return false;

      const readerState = getReaderState(activeReaderId);

      if (!readerState) return false;

      return readerState.paymentStatus === 'starting';
    };

    const isActiveReader = (readerId: string) => {
      const { activeReaderId } = get();
      return activeReaderId === readerId;
    };

    const isIntentCancelled = (readerId: string, paymentIntentId: string) => {
      const readerState = getReaderState(readerId);
      if (!readerState) {
        throw new Error(`Reader with id ${readerId} is not registered, can't check if intent is cancelled`);
      }

      return readerState.canceledStatus === 'complete' && readerState.paymentIntentId === paymentIntentId;
    };

    const hasActiveReader = () => {
      const { activeReaderId } = get();
      return activeReaderId !== null;
    };

    const isCanceledOrCanceling = (readerId: string) => {
      const readerState = getReaderState(readerId);
      if (!readerState) {
        return false;
      }

      return readerState.canceledStatus !== null;
    };

    /***********************************
     * ACTIVE READER WRAPPER METHODS
     **********************************/

    const isActiveReaderInRestrictedState = () => {
      const { activeReaderId } = get();
      if (!activeReaderId) {
        return false;
      }

      return isReaderInRestrictedState(activeReaderId);
    };
    const hasActiveReaderStartedCollectingPayment = () => {
      const activeReader = getActiveReader();
      if (!activeReader) return false;
      return hasStartedCollectingPayment(activeReader.reader.readerId);
    };

    const isActiveReaderCollectingPayment = () => {
      const activeReader = getActiveReader();
      if (!activeReader) return false;
      return isCollectingPayment(activeReader.reader.readerId);
    };

    const setActiveReaderErrorState = (error: ITerminalStrategyError | null) => {
      const activeReader = getActiveReader();
      if (!activeReader) {
        throw new Error(`No active reader to set error state for`);
      }

      setErrorState(activeReader.reader.readerId, error);
    };
    const setActiveReaderSetStartCollecting = () => {
      const activeReader = getActiveReader();
      if (!activeReader) {
        throw new Error(`No active reader to set start collecting state for`);
      }

      setStartCollecting(activeReader.reader.readerId, 'collecting');
    };

    const setActiveReaderSetStopCollecting = (
      paymentStatus: Extract<ReaderState['paymentStatus'], 'complete'> | null
    ) => {
      const activeReader = getActiveReader();
      if (!activeReader) {
        throw new Error(`No active reader to set stop collecting state for`);
      }

      setStopCollecting(activeReader.reader.readerId, paymentStatus);
    };
    const isActiveReaderIntentCancelled = (paymentIntentId: string) => {
      const activeReader = getActiveReader();
      if (!activeReader) {
        return false;
      }

      return isIntentCancelled(activeReader.reader.readerId, paymentIntentId);
    };
    const setActiveReaderForRetryPayment = () => {
      const activeReader = getActiveReader();
      if (!activeReader) {
        throw new Error(`No active reader to set for retry payment`);
      }

      setForRetryPayment(activeReader.reader.readerId);
    };

    const isActiveReaderCanceledOrCanceling = () => {
      const { activeReaderId } = get();
      if (!activeReaderId) {
        return false;
      }

      return isCanceledOrCanceling(activeReaderId);
    };

    const isActiveReaderStartingCollection = () => {
      const { activeReaderId } = get();
      if (!activeReaderId) {
        return false;
      }

      return isStartingCollection(activeReaderId);
    };

    return {
      activeReaderId: null,
      nextReaderId: null,
      readers: {},
      getReaderState,
      getActiveReader,
      setActiveReader,
      unsetActiveReader,
      setReaderStatus,
      canCancelReaderPayment,
      isCollectingPayment,
      isActiveReader,
      getAllReaders,
      hasActiveReader,
      cancelOngoingPayment,
      clearAllReaders,
      cancelAllOngoingPayments,
      isReaderInRestrictedState,
      isCanceledOrCanceling,
      setInitializingReaderId,
      isInitializingReader,
      isPaymentIntentStale,
      activeReaderMethods: {
        setErrorState: setActiveReaderErrorState,
        setStartCollecting: setActiveReaderSetStartCollecting,
        setStopCollecting: setActiveReaderSetStopCollecting,
        isCollectingPayment: isActiveReaderCollectingPayment,
        isIntentCancelled: isActiveReaderIntentCancelled,
        setForRetryPayment: setActiveReaderForRetryPayment,
        hasStartedCollectingPayment: hasActiveReaderStartedCollectingPayment,
        isInRestrictedState: isActiveReaderInRestrictedState,
        isCanceledOrCanceling: isActiveReaderCanceledOrCanceling,
        isStartingCollection: isActiveReaderStartingCollection,
      },
    };
  },
  {
    name: 'TerminalStatusStore',
    trace: true,
  }
);

export type UseTerminalStatusStoreProps = {
  locationId?: string;
};
export const useTerminalStatusStore = ({ locationId }: UseTerminalStatusStoreProps) => {
  const { optimisticRemoveReaderStatus, optimisticAddReaderWaitingInputStatus } =
    useOptimisticResetDiscoverReaderQuery(locationId);

  const { invalidateQuery } = useInvalidateDiscoverReaderQuery(locationId);

  useEffect(() => {
    /**
     * Optimistically add/remove readers' "in use state".
     * These values change when polling updates, so they should be safe to apply optimistically.
     */
    const unsubscribe = terminalStatusStore.subscribe(
      (state: TerminalStatusStore) => state.readers,
      (state, oldData) => {
        const newlyCancelledReaders = Object.values(state).filter(
          (reader) =>
            reader.canceledStatus === 'complete' && oldData[reader.reader.readerId]?.canceledStatus !== 'complete'
        );

        const newlyInUseReaders = Object.values(state).filter(
          (reader) => reader.status === 'waiting-input' && oldData[reader.reader.readerId]?.status !== 'waiting-input'
        );

        const newlyProcessingReaders = Object.values(state).filter(
          (reader) => reader.status === 'processing' && oldData[reader.reader.readerId]?.status !== 'processing'
        );

        if (newlyCancelledReaders.length > 0) {
          for (const reader of newlyCancelledReaders) {
            optimisticRemoveReaderStatus(reader.reader.readerId);
          }
        }

        const processingOrWaitingReaders = Array.from(
          new Set<string>([...newlyInUseReaders, ...newlyProcessingReaders].map((r) => r.reader.readerId))
        );

        if (processingOrWaitingReaders.length > 0) {
          for (const reader of newlyInUseReaders) {
            optimisticAddReaderWaitingInputStatus(reader.reader.readerId);
          }
        }
      }
    );

    return () => unsubscribe(); // Clean up subscription on unmount
  }, [locationId, optimisticRemoveReaderStatus, optimisticAddReaderWaitingInputStatus, invalidateQuery]);

  return terminalStatusStore();
};
